/* Two-part form: search TVmaze for a show, review details + config, then submit. */

import { useState } from "react";
import { createPortal } from "react-dom";
import { useAction, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Search, Tv2, Star, TrendingUp } from "lucide-react";
import { TvShowBrowsePopular } from "@/components/tv-shows/TvShowBrowsePopular";
import { posthog } from "@/lib/posthog";
import { Events } from "@/lib/posthogEvents";
import type { PopularTvShow } from "@/types/tvShowDiscover";

interface ShowSearchResult {
  tvMazeId: number;
  title: string;
  year: string | null;
  poster: string | null;
  network: string | null;
  genres: string[];
  status: string;
  rating: number | null;
  summary: string | null;
}

interface ShowWithEpisodes {
  show: {
    tvMazeId: number;
    title: string;
    year: string | null;
    poster: string | null;
    network: string | null;
    genres: string[];
    status: string;
    rating: number | null;
    summary: string | null;
  };
  episodes: Array<{
    season: number;
    episode: number;
    title: string;
    airdate: string;
    runtime: number | null;
  }>;
}

interface AddTvShowFormProps {
  onSuccess: () => void;
  footerContainer?: HTMLElement | null;
}

function stripHtmlTags(html: string): string {
  return html.replace(/<[^>]*>/g, "").trim();
}

export function AddTvShowForm({ onSuccess, footerContainer }: AddTvShowFormProps) {
  const searchShows = useAction(api.tvShows.searchShows);
  const getShowWithEpisodes = useAction(api.tvShows.getShowWithEpisodes);
  const createShow = useMutation(api.tvShows.create);

  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState<ShowSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [selectedTvMazeId, setSelectedTvMazeId] = useState<number | null>(null);
  const [details, setDetails] = useState<ShowWithEpisodes | null>(null);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);

  const [episodesPerDay, setEpisodesPerDay] = useState(1);
  const [startImmediately, setStartImmediately] = useState(true);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showPopular, setShowPopular] = useState(false);

  const resolveTvMazeId = useAction(api.tvShowDiscover.resolveTvMazeId);

  async function handleSelectPopularShow(show: PopularTvShow) {
    setShowPopular(false);
    setSelectedTvMazeId(null);
    setIsFetchingDetails(true);
    try {
      const tvMazeId = await resolveTvMazeId({
        title: show.title,
        year: show.year || undefined,
      });
      if (tvMazeId) {
        setSelectedTvMazeId(tvMazeId);
        const showDetails = await getShowWithEpisodes({ tvMazeId });
        setDetails(showDetails);
      } else {
        setDetails(null);
      }
    } catch {
      setDetails(null);
    } finally {
      setIsFetchingDetails(false);
    }
  }

  async function handleSearch() {
    const trimmed = searchQuery.trim();
    if (!trimmed) return;
    setShowPopular(false);

    setIsSearching(true);
    setSearchError(null);
    try {
      const data = await searchShows({ query: trimmed });
      setResults(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Search failed";
      setSearchError(message);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }

  function handleSearchKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  }

  async function handleSelectShow(show: ShowSearchResult) {
    setSelectedTvMazeId(show.tvMazeId);
    setIsFetchingDetails(true);
    try {
      const showDetails = await getShowWithEpisodes({ tvMazeId: show.tvMazeId });
      setDetails(showDetails);
    } catch {
      setDetails(null);
    } finally {
      setIsFetchingDetails(false);
    }
  }

  async function handleSubmit() {
    if (!details) return;
    setIsSubmitting(true);
    setSubmitError(null);

    const { show, episodes } = details;
    const totalSeasons = episodes.length > 0
      ? Math.max(...episodes.map((ep) => ep.season))
      : 0;

    try {
      await createShow({
        tvMazeId: show.tvMazeId,
        title: show.title,
        posterUrl: show.poster ?? undefined,
        network: show.network ?? undefined,
        rating: show.rating ?? undefined,
        genres: show.genres,
        showStatus: show.status ?? undefined,
        totalSeasons,
        totalEpisodes: episodes.length,
        summary: show.summary ? stripHtmlTags(show.summary) : undefined,
        episodesPerDay,
        startImmediately,
        episodes: JSON.stringify(episodes),
      });
      posthog.capture(Events.TV_SHOW_ADDED, { title: show.title, tvMazeId: show.tvMazeId, startImmediately });
      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to add TV show";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="space-y-4 pt-2">
      {/* Search section */}
      <div className="space-y-2">
        <Label>Search TV Shows</Label>
        <div className="flex gap-2">
          <Input
            placeholder="Search by show title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            autoComplete="off"
          />
          <Button
            variant="outline"
            onClick={handleSearch}
            disabled={isSearching || !searchQuery.trim()}
          >
            {isSearching ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
          </Button>
        </div>
        {searchError && <p className="text-destructive text-sm">{searchError}</p>}
      </div>

      {/* Browse Popular toggle */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowPopular(!showPopular)}
        className="w-full"
      >
        <TrendingUp className="size-4" />
        {showPopular ? "Hide Popular TV Shows" : "Browse Popular TV Shows"}
      </Button>

      {/* Popular TV shows section */}
      {showPopular && (
        <TvShowBrowsePopular onSelectShow={handleSelectPopularShow} />
      )}

      {/* Search results — horizontal scroll of poster cards */}
      {results.length > 0 && !showPopular && (
        <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
          {results.map((show) => (
            <button
              key={show.tvMazeId}
              type="button"
              className={`flex flex-col items-center gap-1 p-2 rounded-md border cursor-pointer hover:bg-muted/50 shrink-0 w-[90px] ${selectedTvMazeId === show.tvMazeId ? "border-primary bg-muted/50" : "border-border"}`}
              onClick={() => handleSelectShow(show)}
            >
              {show.poster ? (
                <img
                  src={show.poster}
                  alt={show.title}
                  className="w-12 h-[72px] rounded-sm object-cover bg-muted"
                />
              ) : (
                <div className="w-12 h-[72px] rounded-sm bg-muted flex items-center justify-center">
                  <Tv2 className="size-5 text-muted-foreground/50" />
                </div>
              )}
              <span className="text-xs font-medium leading-tight text-center line-clamp-2 w-full">
                {show.title}
              </span>
              <span className="text-[10px] text-muted-foreground text-center truncate w-full">
                {show.year ?? "N/A"}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Loading details */}
      {isFetchingDetails && (
        <div className="flex justify-center py-4">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Show details preview */}
      {details && !isFetchingDetails && (() => {
        const { show, episodes } = details;
        const totalSeasons = episodes.length > 0
          ? Math.max(...episodes.map((ep) => ep.season))
          : 0;
        return (
          <div className="space-y-3 rounded-lg border border-border p-3">
            <div className="flex gap-3">
              {show.poster ? (
                <img
                  src={show.poster}
                  alt={show.title}
                  className="w-20 h-[120px] rounded-sm object-cover bg-muted shrink-0"
                />
              ) : (
                <div className="w-20 h-[120px] rounded-sm bg-muted flex items-center justify-center shrink-0">
                  <Tv2 className="size-6 text-muted-foreground/50" />
                </div>
              )}
              <div className="flex flex-col gap-1.5 min-w-0">
                <p className="text-sm font-semibold leading-tight">{show.title}</p>
                {show.network && (
                  <p className="text-xs text-muted-foreground">{show.network}</p>
                )}
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {show.rating && (
                    <span className="inline-flex items-center gap-0.5">
                      <Star className="size-3 fill-current text-yellow-500" />
                      {show.rating}/10
                    </span>
                  )}
                  <span>{show.status}</span>
                </div>
                {show.genres.length > 0 && (
                  <p className="text-xs text-muted-foreground">{show.genres.join(", ")}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {totalSeasons} season{totalSeasons !== 1 ? "s" : ""} · {episodes.length} episode{episodes.length !== 1 ? "s" : ""}
                </p>
              </div>
            </div>

            {show.summary && (
              <p className="text-xs text-muted-foreground leading-relaxed">
                {stripHtmlTags(show.summary)}
              </p>
            )}

            {/* Configuration */}
            <div className="space-y-3 pt-1 border-t border-border">
              <div className="flex items-center gap-3">
                <Label htmlFor="episodesPerDay" className="text-xs shrink-0">
                  Episodes per day
                </Label>
                <Input
                  id="episodesPerDay"
                  type="number"
                  min={1}
                  max={episodes.length}
                  value={episodesPerDay}
                  onChange={(e) => setEpisodesPerDay(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  className="h-7 w-16 text-xs px-2 text-center"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={startImmediately}
                  onChange={(e) => setStartImmediately(e.target.checked)}
                  className="accent-primary"
                />
                <span className="text-xs text-muted-foreground">Start watching immediately</span>
              </label>
            </div>
          </div>
        );
      })()}

      {submitError && <p className="text-destructive text-sm">{submitError}</p>}

      {footerContainer ? createPortal(
        <Button onClick={handleSubmit} disabled={!details || isSubmitting}>
          {isSubmitting && <Loader2 className="size-4 animate-spin" />}
          Add TV Show
        </Button>,
        footerContainer,
      ) : (
        <Button onClick={handleSubmit} disabled={!details || isSubmitting} className="w-full">
          {isSubmitting && <Loader2 className="size-4 animate-spin" />}
          Add TV Show
        </Button>
      )}
    </div>
  );
}
